import * as React from 'react';
import * as mrCap from '../../mrCapTypes';
import * as api from '../../api/booking';
import { className, KeyCodes, autobind, getShopBySlug, getShopSlug } from '../../util';
import { ViewProp } from '..';
import { ProductFilter } from './filter';
import { RegNrBox } from './regNr';
import { Product } from './product';
import { PageIntro } from '../pageintro';
import { Portal } from '../portal';
import { CarDetailsBox } from './carDetailsBox';
import { CallWindow } from './callWindow';

interface Props extends ViewProp {
}

interface State {
  productsResponse?: mrCap.ProductsResponse;
  productRules: string[];
  loading: boolean;
  filterBy?: string;
  callPortal: boolean;
  otherServices: boolean;
  readmoreOk: boolean;
  shops: mrCap.Shop[];
  userSearched: boolean;
  showLinkedProduct?: string | null;
}

export class ProductSelection extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
      productRules: [],
      callPortal: false,
      otherServices: false,
      readmoreOk: true,
      shops: [],
      userSearched: false
    };
  }

  componentDidMount() {
    this.fetchProducts();
    this.fetchRules();
    this.fetchShops();

    document.addEventListener('keydown', this.handleKeyDown);
  }

  async fetchShops() {
    const shops = await api.getShops();

    if (shops && shops.length) {
      this.setState({ shops });
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.selectedProducts.length !== this.props.selectedProducts.length) {
      this.fetchRules();
    }
  }

  async fetchProducts() {

    const productsResponse = await api.getProducts(this.props.selectedShop!.id, this.props.regNr);

    if (productsResponse && productsResponse.products.length) {
      const initialGroup = this.props.initialFilterArticleGroup;

      this.setState({
        productsResponse,
        filterBy: productsResponse.products.some(p => p.group === initialGroup)
          ? initialGroup
          : productsResponse.products[0].group ?? ''
      });

      if (productsResponse.campaign) {
        this.props.setCampaign(productsResponse.campaign);
      }
    }
  }

  async fetchRules() {
    if (!this.props.selectedProducts.length) {
      if (this.state.productRules.length) {
        this.setState({ productRules: [] });
      }
      return;
    }

    try {
      this.setState({ loading: true });
      const rules = await api.getProductRules(this.props.selectedProducts.map(p => p.articleNr));
      this.setState({ productRules: rules });
    } finally {
      this.setState({ loading: false });
    }
  }

  async fetchCarDetails(regNr: string) {
    if (this.props.regNr) {
      try {
        const carDetails = await api.getCarDetails(regNr);
        this.props.setCarDetails(carDetails);
        this.fetchProducts();

        this.setState({ userSearched: true });
      } catch {
        this.setState({ userSearched: true });
      }
    }
  }

  handleSelectFilterOption = (group: string) => {
    this.setState({ filterBy: this.state.filterBy === group ? undefined : group });
  };

  handleGetCarDetails = (regNr: string) => {
    this.fetchCarDetails(regNr);
  };

  clearCarDetails = () => {
    this.props.setRegNr('');
    this.props.setCarDetails(undefined);
    this.setState({
      userSearched: false
    }, () => {this.fetchProducts();});
  };

  showLinkedReadmore = (product: mrCap.Product) => {
    return (artNr: string) => {
      this.setState({ showLinkedProduct: artNr }, () => {
        this.setState({ showLinkedProduct: null });
      });
    };
  };

  @autobind
  handleKeyDown(e: KeyboardEvent) {
    if (e.keyCode === KeyCodes.enter) {
      e.preventDefault();
      if (this.props.regNr) {
        this.fetchCarDetails(this.props.regNr);
      }
    }
  }

  render() {
    const { loading, productRules, filterBy, productsResponse } = this.state;
    const products = productsResponse?.products ?? [];

    let stepOnePreamble = this.props.texts.stepOneParagraph;

    if (this.props.selectedShop?.slug === getShopSlug()) {
      stepOnePreamble = `<strong class="selected-station">Vald anläggning: ${this.props.selectedShop.name}</strong>` + stepOnePreamble;
    }

    return (
      <div className='outer-page'>
        <div className={className('product-selection-container', { 'products-loading': loading }, 'page')}>
          <div className='page-top-content'>

            <PageIntro
              header={this.props.texts.stepOneHeader}
              preamble={stepOnePreamble}/>

            <div className='regnr-container'>
              <RegNrBox
                initialValue={this.props.regNr}
                regNrSelected={this.props.setRegNr}
                header={this.props.texts.stepOneFormHeader}
                desc={this.props.texts.stepOneFormDesc}
                getCarDetails={(regNr) => this.handleGetCarDetails(regNr)}
                userSearched={this.state.userSearched} />

              {this.props.regNr &&
                <div className={className('outer-regnr-container', {visible: this.props.regNr !== ''})}>
                  {this.props.carDetails &&
                    <div className='search-result'>
                      <CarDetailsBox
                        regnr={this.props.regNr}
                        carDetails={this.props.carDetails} />
                      <div className='cta-btn' onClick={this.clearCarDetails}>Sök på nytt registreringsnummer</div>
                      {this.props.carDetails.weight > this.props.weightThreshold &&
                        <div className='vehicle-too-heavy'>
                          <h3>Din bil är unik!</h3>
                          <span>Vänligen kontakta din lokala anläggning för prisuppgift för just ditt fordon.</span>
                        </div>}
                    </div>}
                  {!this.props.carDetails && this.state.userSearched &&
                    <div className='search-result'>
                      <span className='nothing-found'>
                        <i className='fas fa-times'></i> Vi kunde inte hitta någon information på registreringsnumret
                        <strong> {this.props.regNr.toUpperCase()}</strong>
                      </span>
                      <div className='cta-btn' onClick={this.clearCarDetails}>Sök på nytt registreringsnummer</div>
                    </div>}
                </div>
              }
            </div>

          </div>

          <div>
            <ProductFilter
              filterBy={filterBy}
              filterOptions={products}
              select={this.handleSelectFilterOption} />

            <div className='products'>
              {!products.length && <>
                <div className='no-products'
                  dangerouslySetInnerHTML={{__html: this.props.texts.stepOneNoProducts}}>
                </div>
              </>}

              {products.map(product =>
                <Product
                  key={product.articleNr}
                  selected={this.props.selectedProducts.some(p => p.articleNr === product.articleNr)}
                  disabled={
                    product.type === mrCap.ArticleType.treatment
                    && productRules.length > 0
                    && !productRules.some(p => p === product.articleNr)}
                  visible={filterBy ? product.group === filterBy : true}
                  toggle={this.props.toggleProduct}
                  campaign={productsResponse?.campaign}
                  product={product}
                  updateSelectedProduct={this.props.updateProduct}
                  upsellProduct={
                    product.upsellArticleNr && product.upsellArticleNr.length > 0
                      ? products.find(prod => prod.articleNr === product.upsellArticleNr)
                      : null}
                  downsellProduct={
                    product.downsellArticleNr && product.downsellArticleNr.length > 0
                      ? products.find(prod => prod.articleNr === product.downsellArticleNr)
                      : null}
                  showLinkedReadmore={this.showLinkedReadmore(product)}
                  showReadmore={
                    this.state.showLinkedProduct && this.state.showLinkedProduct === product.articleNr
                      ? true
                      : undefined}
                  vehicle={this.props.carDetails} />)}
            </div>
          </div>

          {this.state.shops && <>
            <Portal
              visible={this.state.callPortal}
              onClose={() => this.setState({callPortal: false, readmoreOk: true})}>

              <PageIntro
                header={this.props.texts.stepOneCallHeader}
                preamble={this.props.texts.stepOneCallParagraph} />

              <CallWindow
                shops={this.state.shops}
                gdprText={this.props.texts.stepThreeGdpr} />

            </Portal>
          </>}

          <Portal
            visible={this.state.otherServices}
            onClose={() => this.setState({otherServices: false, readmoreOk: true})}>

            <div className='any-content'>
              <PageIntro
                header={this.props.texts.stepOneOtherServicesHeader}
                preamble={this.props.texts.stepOneOtherServicesContent} />
            </div>

          </Portal>
        </div>

        <div onClick={() => this.setState({callPortal: true, readmoreOk: false})}
          className='cta-btn left-btn'>Jag vill bli uppringd</div>

        <div onClick={() => this.setState({otherServices: true, readmoreOk: false})}
          className='cta-btn left-btn'>{this.props.texts.stepOneOtherServicesHeader}</div>

      </div>
    );
  }
}